import type { MantineTheme } from '@mantine/core';
import { Box, createStyles, Group, Stack } from '@mantine/core';
import { useGetAiAccessStatus, useGetPersonas } from '@repo/api-codegen';
import { trimMarkdown } from '@repo/common/utils';
import { IconButton } from '@repo/foundations';
import { modifyOpacityOfRgbaColor } from '@repo/theme/utils';
import {
	type KeyboardEvent,
	memo,
	useCallback,
	useMemo,
	useRef,
	useState,
} from 'react';
import type { SecodaEditor } from '../../Editor/SecodaEditor';
import { RichEditor } from '../../RichEditor';
import { useAIAssistantContext } from '../hooks';
import { AIAssistantMode } from '../types';
import { AIInputToggleButton } from './AIInputToggleButton';
import { AttachmentButton } from './AttachmentButton';
import { Attachments } from './Attachments';
import type { AIPromptInputProps } from './types';

const SUBMIT_ICON_WIDTH = '2rem';

const useStyles = createStyles((theme: MantineTheme) => ({
	container: {
		gap: 0,
		borderRadius: theme.radius.md,
		boxShadow: `0px 0px 0px 1px ${theme.other.getColor('shadow/border')},
   0px 1px 4px 0px ${modifyOpacityOfRgbaColor(theme.other.getColor('shadow/button-shadow'), 0.08)},
   0px 4px 8px 0px ${modifyOpacityOfRgbaColor(theme.other.getColor('shadow/button-shadow'), 0.08)}`,
		border: 'none',
		backgroundColor: theme.other.getColor('surface/primary/default'),
		marginRight: `calc(${theme.spacing.xs} * -1)`,
		marginLeft: `calc(${theme.spacing.xs} * -1)`,
	},
	controls: {
		padding: `0 ${theme.spacing.xs} ${theme.spacing.xs} ${theme.spacing.xs}`,
		justifyContent: 'space-between',
		alignItems: 'flex-end',
		flexWrap: 'nowrap',
	},
	input: {
		// CSS for RichEditor
		flex: '1 !important', // The editor forces `flex-grow: 0` when in readOnly mode
		maxWidth: `calc(100% - ${SUBMIT_ICON_WIDTH})`,
		padding: theme.spacing.md,
		maxHeight: '300px',
		overflowY: 'auto',

		'&:hover,&:focus': {
			'&::-webkit-scrollbar-thumb': {
				backgroundColor: theme.other.getColor('fill/primary/selected'),
			},
		},
		// Scrollbar styling
		'&::-webkit-scrollbar': {
			backgroundColor: 'transparent',
			width: theme.spacing.xs,
		},
		'&::-webkit-scrollbar-track': {
			backgroundColor: 'transparent',
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: 'transparent',

			borderRadius: theme.radius.sm,
		},
		'&::-webkit-scrollbar-thumb:hover': {
			backgroundColor: theme.other.getColor('fill/primary/selected'),
		},
		// Outline editor
		'& .ProseMirror > *:last-child': {
			marginBottom: 0,
		},
	},
	sendTooltip: {
		backgroundColor: theme.other.getColor('fill/primary/default'),
		color: theme.other.getColor('text/primary/default'),
		borderRadius: theme.radius.sm,
		border: `1px solid ${theme.other.getColor('border/primary/default')}`,
	},
	inlineControls: {
		position: 'absolute',
		right: theme.spacing.xs,
		top: '50%',
		transform: 'translateY(-50%)',
	},
}));

function AIPromptInputV2Internal({
	value,
	onSubmit,
	onChange,
	isLoading,
	onStop,
	onAttachmentUpload,
	onAttachmentRemove,
	editorReRenderKey,
	attachments,
	activeAiPersonaId,
	initialIsWebSearch = false,
	initialIsChart = false,
	canUseAiChartTool,
}: AIPromptInputProps) {
	const isEmpty = !trimMarkdown(value);

	const { data: personas } = useGetPersonas({});
	const containerRef = useRef<HTMLDivElement>(null);

	const activePersona = useMemo(
		() => personas?.find((p) => p.id === activeAiPersonaId),
		[personas, activeAiPersonaId]
	);

	const { classes, cx } = useStyles();

	const { mode } = useAIAssistantContext();

	const { data } = useGetAiAccessStatus({});

	const [isWebSearch, setIsWebSearch] = useState<boolean>(initialIsWebSearch);
	const [isChart, setIsChart] = useState<boolean>(initialIsChart);

	const handleChange = useCallback(
		(input?: string) => {
			onChange(input || '');
		},
		[onChange]
	);

	const handleSubmit = useCallback(() => {
		onSubmit(
			undefined,
			false,
			isWebSearch,
			canUseAiChartTool ? isChart : false
		);
	}, [onSubmit, isWebSearch, isChart, canUseAiChartTool]);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (!isEmpty && event.key === 'Enter' && !event.shiftKey) {
				event.preventDefault();
				handleSubmit();
			}
		},
		[isEmpty, handleSubmit]
	);

	const editorRef = useRef<SecodaEditor>(null);

	const handleMentionClick = useCallback(() => {
		if (!editorRef.current) {
			return;
		}

		editorRef.current.triggerMentionMenu();
	}, []);

	const handleClickContainer = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			if (
				event.target === containerRef.current ||
				(event.target as HTMLElement).classList.contains('focus-input')
			) {
				editorRef.current?.focus();
			}
		},
		[editorRef]
	);

	const isSidebar = mode === AIAssistantMode.SIDEBAR;
	const actionIconSize = isSidebar ? 'sm' : 'md';

	return (
		<Stack
			ref={containerRef}
			className={`${classes.container} focus-input`}
			onClick={handleClickContainer}
			spacing="xs"
		>
			{!!attachments?.length && (
				<Attachments
					attachments={attachments}
					onAttachmentRemove={onAttachmentRemove}
				/>
			)}
			<Box
				className={cx(classes.input, 'ai-prompt-input focus-input')}
				onKeyDown={handleKeyDown}
			>
				<RichEditor
					key={`${editorReRenderKey}-${activePersona?.id}`}
					dataTestId="ai-prompt-input"
					initialValue={value}
					emptyDocPlaceholder={
						activePersona?.id
							? `Ask ${activePersona.name || 'New persona'}...`
							: 'Ask Secoda AI...'
					}
					onChangeCallback={handleChange}
					readOnly={isLoading}
					disableTopGap
					disableExtensions={[
						'emoji',
						'emojimenu',
						'query_block',
						'chart_block',
						'image',
						'embed',
						'holy',
						'page',
						'attachment',
						'comment',
						'blockmenu',
					]}
					singleLineEditor
					disableResourceLinking
					disableChangeCallbackDebounce
					autoFocus
					editorRef={editorRef}
				/>
			</Box>
			<Group className={`${classes.controls} focus-input`}>
				<Group spacing="3xs" style={{ flex: 1 }} className="focus-input">
					<IconButton
						iconName="at"
						variant="tertiary"
						onClick={handleMentionClick}
						tooltip="Mention resource or user"
						tooltipProps={{
							offset: 8,
						}}
					/>
					<AttachmentButton
						attachments={attachments ?? []}
						onAttachmentUpload={onAttachmentUpload}
					/>
					<AIInputToggleButton
						label="Web search"
						icon="world"
						active={isWebSearch}
						onToggle={setIsWebSearch}
					/>
					{canUseAiChartTool && (
						<AIInputToggleButton
							label="Chart"
							icon="chartBar"
							active={isChart}
							onToggle={setIsChart}
						/>
					)}
				</Group>
				<Box className="focus-input">
					{isLoading ? (
						<IconButton
							tooltip="Stop"
							tooltipProps={{
								withArrow: true,
								arrowSize: 8,
								position: 'top',
							}}
							iconName="playerStop"
							data-testid="stop-button"
							size={actionIconSize}
							onClick={onStop}
							variant="primary"
						/>
					) : (
						<IconButton
							tooltip="Send message (Enter)"
							tooltipProps={{
								withArrow: true,
								arrowSize: 8,
								position: 'top',
								disabled: isEmpty,
							}}
							variant="primary"
							iconName="arrowUp"
							data-testid="submit-button"
							disabled={isEmpty || data?.restriction_info.is_restricted}
							size={actionIconSize}
							display="block"
							onClick={handleSubmit}
						/>
					)}
				</Box>
			</Group>
		</Stack>
	);
}

export const AIPromptInputV2 = memo(AIPromptInputV2Internal);
